import { type RefObject, useCallback, useEffect, useState } from 'react'

import { ACTIVE_SECTION_TOLERANCE_PX, SECTION_ID_SEPARATOR } from './consts'
import { getScrollParent, getStickyOffset } from './utils'

export const useActiveAnchor = (sectionIds: string[], navRef: RefObject<HTMLElement | null>) => {
  const [activeId, setActiveId] = useState<string>()

  // Keyed on the ids' contents, not the array identity, so the listener below is registered once
  // per section list rather than once per render of the caller.
  const sectionIdsKey = sectionIds.join(SECTION_ID_SEPARATOR)

  const syncActiveId = useCallback(() => {
    const nav = navRef.current
    if (!nav) return

    const ids = sectionIdsKey ? sectionIdsKey.split(SECTION_ID_SEPARATOR) : []
    const referenceLine = nav.getBoundingClientRect().top
    const reached = ids.filter((id) => {
      const section = document.getElementById(id)
      if (!section) return false
      return section.getBoundingClientRect().top - referenceLine <= ACTIVE_SECTION_TOLERANCE_PX
    })

    setActiveId(reached[reached.length - 1] ?? ids[0])
  }, [navRef, sectionIdsKey])

  useEffect(() => {
    syncActiveId()

    document.addEventListener('scroll', syncActiveId, { capture: true, passive: true })
    window.addEventListener('resize', syncActiveId)

    return () => {
      document.removeEventListener('scroll', syncActiveId, { capture: true })
      window.removeEventListener('resize', syncActiveId)
    }
  }, [syncActiveId])

  const scrollToSection = useCallback(
    (id: string) => {
      const nav = navRef.current
      const section = document.getElementById(id)
      if (!nav || !section) return

      const scrollParent = getScrollParent(nav)
      const parentTop =
        scrollParent === document.documentElement ? 0 : scrollParent.getBoundingClientRect().top
      // Target the nav's stuck position, which is where syncActiveId draws its reference line.
      const offset = section.getBoundingClientRect().top - parentTop - getStickyOffset(nav)

      scrollParent.scrollTo({ top: scrollParent.scrollTop + offset, behavior: 'smooth' })
      setActiveId(id)
    },
    [navRef],
  )

  return { activeId, scrollToSection }
}
